import React, { useState } from "react";
import { Star, StarHalf } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  value: number;  // Current rating value (0-5)
  totalRatings?: number;  // Optional total number of ratings
  size?: number;  // Size of stars in pixels
  color?: string;  // Color of filled stars
  readOnly?: boolean;  // If true, user cannot change rating
  showCount?: boolean;  // If true, show total ratings count
  onChange?: (rating: number) => void;  // Callback when rating changes
  className?: string;  // Additional classes
}

export default function StarRating({
  value,
  totalRatings,
  size = 20,
  color = "text-yellow-400",
  readOnly = false,
  showCount = false,
  onChange,
  className
}: StarRatingProps) {
  const [hoverRating, setHoverRating] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  
  // Returns stars array with actual or hover rating
  const displayValue = isHovering ? hoverRating : value;
  
  // Create array of 5 stars
  const stars = Array.from({ length: 5 }, (_, i) => i + 1);
  
  // Handle mouse enter on a star
  const handleMouseEnter = (rating: number) => {
    if (readOnly) return;
    setHoverRating(rating);
    setIsHovering(true);
  };
  
  // Handle mouse leave
  const handleMouseLeave = () => {
    if (readOnly) return;
    setIsHovering(false);
  };
  
  // Handle click on a star
  const handleClick = (rating: number) => {
    if (readOnly) return;
    onChange?.(rating);
  };
  
  return (
    <div 
      className={cn("flex items-center", className)}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex">
        {stars.map((rating) => (
          <div
            key={rating}
            onClick={() => handleClick(rating)}
            onMouseEnter={() => handleMouseEnter(rating)}
            className={cn(
              "cursor-pointer transition-colors duration-100",
              readOnly && "cursor-default"
            )}
            style={{ width: size, height: size }}
          >
            {/* Full star if rating >= star, empty star if rating < star - 0.5, half star otherwise */}
            <Star
              fill={displayValue >= rating ? "currentColor" : "none"}
              stroke="currentColor"
              className={cn(
                color,
                displayValue >= rating - 0.5 && displayValue < rating && "hidden"
              )}
              width={size}
              height={size}
            />
            {/* Half star */}
            {displayValue >= rating - 0.5 && displayValue < rating && (
              <StarHalf
                fill="currentColor"
                stroke="currentColor"
                className={color}
                width={size}
                height={size}
              />
            )}
          </div>
        ))}
      </div>
      
      {/* Display count if enabled */}
      {showCount && totalRatings !== undefined && (
        <span className="ml-2 text-sm text-zinc-400">
          ({totalRatings})
        </span>
      )}
    </div>
  );
}
